import { useTranslation } from "@shared/utils/internationalization";
import * as yup from "yup";
import { useCallback, useMemo } from "react";
import { TicketTypeSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { useGlobalAuthorization } from "@shared/modules/auth/contexts/GlobalAuthorizationProvider/GlobalAuthorizationProvider";
import { AuthorizationType } from "@shared/modules/auth/contexts/GlobalAuthorizationProvider/types";
import { showItemPasswordInput } from "@shared/utils/password/utils";

export function useItemPasswordEntry(
  item: SeatingItemPropertyMap | TicketTypeSerializer,
  authArray: Array<{ id: number; authorized: boolean }>,
  updateAuthArray: (
    inputArray: Array<{ id: number; authorized: boolean }>
  ) => void,
  slugToCheck: string
) {
  const { t } = useTranslation("common");

  const passwordSchema = useMemo(
    () =>
      yup.object().shape({
        password: yup.string().required(t("Password is required")),
      }),
    [t]
  );

  const { authorize } = useGlobalAuthorization();

  const isPasswordProtected = useMemo(
    () => showItemPasswordInput(item, authArray, updateAuthArray),
    [item, authArray, updateAuthArray]
  );
  const isUnauthorized = useMemo(
    () =>
      authArray.find(
        (authItem) => authItem.id === item.id && !authItem.authorized
      ),
    [authArray, item.id]
  );

  const onSubmit = useCallback(
    (
      { password }: { password: string },
      { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
      authorize(
        AuthorizationType.TICKET_TYPE_PASSWORD,
        item.id.toString(),
        password,
        { eventSlug: slugToCheck }
      )
        .then((res) => {
          if (res.authorized) {
            updateAuthArray(
              authArray.map((authItem) =>
                authItem.id === item.id
                  ? { ...authItem, authorized: true }
                  : authItem
              )
            );
          }
        })
        .catch(() => {
          // noop
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [authorize, slugToCheck, authArray, updateAuthArray, item.id]
  );

  return { passwordSchema, isPasswordProtected, isUnauthorized, onSubmit };
}
